import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import {
  Timer, ArrowLeft, DollarSign, Clock, TrendingUp, TrendingDown,
  AlertTriangle, Loader2, RefreshCw, Target, Zap, Calendar
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { useSnowflakeCredentials } from '../components/SnowflakeConnect';
import SnowflakeConnect from '../components/SnowflakeConnect';
import { useDemoData } from '../components/useDemoData';
import EmptyDataState from '../components/EmptyDataState';
import axios from 'axios';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BudgetCountdown = () => {
  const [loading, setLoading] = useState(false);
  const [showConnect, setShowConnect] = useState(false);
  const [budgetData, setBudgetData] = useState(null);
  const [monthlyBudget, setMonthlyBudget] = useState(25000);
  const { credentials, isConnected, updateCredentials } = useSnowflakeCredentials();
  const { shouldShowData } = useDemoData();

  const fetchBudgetData = async () => {
    if (!isConnected) {
      if (shouldShowData) {
        setBudgetData(getMockData());
      }
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/snowflake/advanced/budget-countdown`, {
        credentials,
        monthly_budget: monthlyBudget,
        credit_price: 2.50
      });
      setBudgetData(response.data);
      toast.success('Budget data refreshed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to fetch budget data');
    } finally {
      setLoading(false);
    }
  };

  const getMockData = () => ({
    budget: {
      monthly_budget: monthlyBudget,
      credit_price: 2.50,
      total_spent: 16750,
      budget_remaining: monthlyBudget - 16750,
      percent_used: (16750 / monthlyBudget) * 100
    },
    burn_rate: {
      daily_average_cost: 1200,
      daily_average_credits: 480,
      hourly_current: 50
    },
    forecast: {
      projected_monthly_cost: 28500,
      projected_over_budget: 3500,
      days_until_budget_exhausted: 6.9,
      days_remaining_in_month: 12
    },
    status: "caution",
    status_message: "CAUTION: Projected to exceed budget by $3,500",
    alerts: [
      { severity: "medium", message: "Projected to exceed budget by $3,500" },
      { severity: "high", message: "Budget will be exhausted in 6.9 days at current rate" }
    ],
    daily_trend: [
      { DAY: '1', CREDITS: 850, cost: 2125 },
      { DAY: '5', CREDITS: 920, cost: 2300 },
      { DAY: '10', CREDITS: 1100, cost: 2750 },
      { DAY: '15', CREDITS: 1350, cost: 3375 },
      { DAY: '20', CREDITS: 1200, cost: 3000 },
      { DAY: 'Today', CREDITS: 1450, cost: 3625 },
    ],
    hourly_today: [
      { HOUR: '6am', CREDITS: 20 },
      { HOUR: '9am', CREDITS: 85 },
      { HOUR: '12pm', CREDITS: 120 },
      { HOUR: '3pm', CREDITS: 95 },
      { HOUR: '6pm', CREDITS: 45 },
    ]
  });

  useEffect(() => {
    fetchBudgetData();
  }, [isConnected, monthlyBudget]);

  const handleConnected = (creds) => {
    updateCredentials(creds);
    setShowConnect(false);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'critical': return 'from-red-500 to-red-600';
      case 'warning': return 'from-amber-500 to-orange-500';
      case 'caution': return 'from-yellow-500 to-amber-500';
      default: return 'from-emerald-500 to-green-500';
    }
  };

  const displayData = budgetData || (shouldShowData ? getMockData() : null);

  if (!displayData) {
    return (
      <div className="space-y-6" data-testid="budget-countdown">
        <EmptyDataState title="Budget Countdown" description="Connect your Snowflake account to track budget usage and get countdown alerts." />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="budget-countdown">
      <SnowflakeConnect 
        isOpen={showConnect} 
        onClose={() => setShowConnect(false)}
        onConnected={handleConnected}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/snowflake">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold font-['Manrope'] text-slate-900 dark:text-white flex items-center gap-2">
              <Timer className="w-7 h-7 text-emerald-400" />
              <BrandTitle>Budget Countdown</BrandTitle>
            </h1>
            <p className="text-slate-700 text-sm">Real-time budget tracking with forecasting</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-400">Monthly Budget:</span>
            <Input
              type="number"
              value={monthlyBudget}
              onChange={(e) => setMonthlyBudget(Number(e.target.value))}
              className="w-32"
            />
          </div>
          <Button onClick={fetchBudgetData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Status Banner */}
      <div className={`bg-gradient-to-r ${getStatusColor(displayData.status)} rounded-xl p-6 text-white`}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-2xl font-bold mb-1">{displayData.status_message}</h3>
            <p className="opacity-90">
              {displayData.forecast.days_until_budget_exhausted 
                ? `Budget will be exhausted in ${displayData.forecast.days_until_budget_exhausted.toFixed(1)} days`
                : 'Budget is on track for this month'
              }
            </p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-bold">${displayData.budget.budget_remaining.toLocaleString()}</p>
            <p className="opacity-90">remaining</p>
          </div>
        </div>
      </div>

      {/* Budget Progress */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-slate-400">Budget Progress</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">
                ${displayData.budget.total_spent.toLocaleString()} 
                <span className="text-slate-400 font-normal"> / ${displayData.budget.monthly_budget.toLocaleString()}</span>
              </p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-slate-900 dark:text-white">{displayData.budget.percent_used.toFixed(1)}%</p>
              <p className="text-sm text-slate-400">used</p>
            </div>
          </div>
          <Progress 
            value={Math.min(displayData.budget.percent_used, 100)} 
            className={`h-4 ${displayData.budget.percent_used > 90 ? '[&>div]:bg-red-500' : displayData.budget.percent_used > 75 ? '[&>div]:bg-amber-500' : '[&>div]:bg-emerald-500'}`}
          />
          <div className="flex justify-between mt-2 text-xs text-slate-400">
            <span>$0</span>
            <span>75% (${ (displayData.budget.monthly_budget * 0.75).toLocaleString()})</span>
            <span>90% (${(displayData.budget.monthly_budget * 0.9).toLocaleString()})</span>
            <span>${displayData.budget.monthly_budget.toLocaleString()}</span>
          </div>
        </CardContent>
      </Card>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-blue-900/30 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Daily Burn Rate</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">${displayData.burn_rate.daily_average_cost.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <Clock className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Hourly Rate</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">${displayData.burn_rate.hourly_current}/hr</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <Target className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Projected Total</p>
                <p className="text-xl font-bold text-amber-600">${displayData.forecast.projected_monthly_cost.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-md">
          <CardContent className="p-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Days Left</p>
                <p className="text-xl font-bold text-red-600">
                  {displayData.forecast.days_until_budget_exhausted?.toFixed(1) || '∞'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Daily Spend Trend */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle>Daily Spend Trend</CardTitle>
            <CardDescription>Credit usage this month</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%" minWidth={250} minHeight={150}>
                <AreaChart data={displayData.daily_trend}>
                  <defs>
                    <linearGradient id="spendGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="DAY" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }}
                    formatter={(value) => [`${value.toLocaleString()} credits`, 'Credits']}
                  />
                  <ReferenceLine y={displayData.burn_rate.daily_average_credits} stroke="#f59e0b" strokeDasharray="5 5" label="Avg" />
                  <Area type="monotone" dataKey="CREDITS" stroke="#3b82f6" strokeWidth={2} fill="url(#spendGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card className="border-0 shadow-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Budget Alerts
            </CardTitle>
            <CardDescription>Issues that need attention</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {displayData.alerts.map((alert, idx) => (
                <div key={idx} className={`p-4 rounded-lg border ${
                  alert.severity === 'critical' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
                  alert.severity === 'high' ? 'bg-amber-50 border-amber-200 dark:bg-amber-900/20 dark:border-amber-800' :
                  'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800'
                }`}>
                  <div className="flex items-start gap-2">
                    <AlertTriangle className={`w-5 h-5 flex-shrink-0 ${
                      alert.severity === 'critical' ? 'text-red-500' :
                      alert.severity === 'high' ? 'text-amber-500' : 'text-yellow-500'
                    }`} />
                    <div>
                      <Badge className={`mb-1 ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                        alert.severity === 'high' ? 'bg-amber-100 text-amber-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {alert.severity.toUpperCase()}
                      </Badge>
                      <p className="text-sm text-slate-700 dark:text-slate-300">{alert.message}</p>
                    </div>
                  </div>
                </div>
              ))}
              
              {displayData.forecast.projected_over_budget > 0 && (
                <div className="p-4 rounded-lg bg-slate-100 dark:bg-slate-700/50 text-center">
                  <p className="text-sm text-slate-400 dark:text-slate-400 mb-2">
                    To stay within budget, reduce daily spend to:
                  </p>
                  <p className="text-2xl font-bold text-emerald-600">
                    ${Math.round(displayData.budget.budget_remaining / displayData.forecast.days_remaining_in_month).toLocaleString()}/day
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BudgetCountdown;
